import type { FC } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useLinkComponent } from '@eneco/link';
import { Stack, TextLink } from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { ProfileManagementLoginDetailsRendering } from '@sitecore/types/ProfileManagementLoginDetails';

import ReadFormField from '../../../../../components/ReadFormField';
import ReadWithAction from '../../../../../components/ReadWithAction';
import { ChampionDisabledAction } from '../../../../../components/ChampionDisabledAction';
import { localLoginStackClass } from '../../../../../styles/components.css';

type LocalLoginCardData = {
  hasLocalLogin: boolean;

  displayName: string;
  email: string;
};

type LocalLoginCardProps = {
  data: LocalLoginCardData;
  isLoading: boolean;
};

const EmailStyle = styled('div', {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
});

export const LocalLoginCard: FC<LocalLoginCardProps> = ({ data, isLoading }) => {
  const { f } = useContent<ProfileManagementLoginDetailsRendering>();
  const { isImpersonating } = useSelfServiceAccount();

  const passwordValue = data.hasLocalLogin ? '*******' : f('localLoginCardLoginNotConfiguredText.value');
  const Link = useLinkComponent();

  const email = data.email;
  const emailElement = (
    <Stack className={localLoginStackClass} wrap="false" direction="row">
      <EmailStyle>{email}</EmailStyle>
    </Stack>
  );
  return (
    <ReadWithAction title={f('localLoginCardTitle.value')}>
      <Stack className={localLoginStackClass} direction="column" alignX="start" gap="6">
        <ReadFormField
          isLoading={isLoading}
          label={f('localLoginCardFormDisplayNameText.value')}
          value={data.displayName}></ReadFormField>
        {email[1] && (
          <ReadFormField
            isLoading={isLoading}
            label={f('localLoginCardFormEmailLabel.value')}
            value={emailElement}></ReadFormField>
        )}
        <ReadFormField
          isLoading={isLoading}
          label={f('localLoginCardFormPasswordLabel.value')}
          value={passwordValue}></ReadFormField>
        {isImpersonating() ? (
          <ChampionDisabledAction>
            <TextLink emphasis="high">{f('localLoginCardLink.value.text')}</TextLink>
          </ChampionDisabledAction>
        ) : (
          <Link linkType="internal" href={f('localLoginCardLink.value.href')}>
            <TextLink emphasis="high">{f('localLoginCardLink.value.text')}</TextLink>
          </Link>
        )}
      </Stack>
    </ReadWithAction>
  );
};
